import React, { useEffect, useRef } from 'react';
import { LuDroplets, LuSofa, LuShieldCheck, LuCheck } from 'react-icons/lu';
import './Home.css';
import Seo from '../components/Seo';

const STATS = [
  { value: '500+', label: 'Cars Detailed' },
  { value: '5★',   label: 'Avg. Rating' },
  { value: '100%', label: 'Mobile Service' },
  { value: '3+',   label: 'Years Experience' },
];

const SERVICES_PREVIEW = [
  {
    icon: LuDroplets,
    title: 'Exterior Wash & Wax',
    desc: 'Full hand wash, clay bar treatment, and carnauba wax application for a brilliant shine.',
  },
  {
    icon: LuSofa,
    title: 'Interior Deep Cleaning',
    desc: 'Complete vacuum, steam clean, leather conditioning, and odor elimination.',
  },

  {
    icon: LuShieldCheck,
    title: 'Ceramic Coating',
    desc: 'Professional-grade nano coating for years of hydrophobic protection.',
  },
];

export default function Home({ navigate }) {
  const heroRef = useRef(null);

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    // A11y: skip the parallax motion for users who prefer reduced motion.
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const onScroll = () => {
      const y = window.scrollY;
      hero.style.setProperty('--parallax', `${y * 0.4}px`);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="home">
      <Seo
        title="Cardona Auto Detailing | Mobile Car Detailing in Algood, TN"
        description="Premium mobile car detailing in Cookeville and Algood, TN. Wash, wax, paint correction, and ceramic coating — we come to you."
        path="/"
      />
      {/* HERO */}
      <section className="hero" ref={heroRef}>
        <div className="hero-bg"></div>
        <div className="hero-overlay"></div>
        <div className="container hero-content">
          <p className="hero-eyebrow">Mobile Detailing Service</p>
          <h1 className="hero-title">
            YOUR CAR<br />
            <span className="hero-title-gold">DESERVES</span><br />
            THE BEST
          </h1>
          <p className="hero-sub">
            We come to your home, office, or wherever you are.<br />
            Premium detailing — no trips to a shop.
          </p>
          <div className="hero-actions">
            <button className="btn-primary" onClick={() => navigate('reservations')}>
              Book a Detail →
            </button>
            <button className="btn-outline" onClick={() => navigate('services')}>
              See Services
            </button>
          </div>
        </div>
        <div className="hero-scroll-hint">
          <span></span>
          <p>Scroll</p>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="stats-strip">
        <div className="container stats-grid">
          {STATS.map((s, i) => (
            <div className="stat-item" key={i}>
              <strong>{s.value}</strong>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="page-section services-section">
        <div className="container">
          <p className="section-label">What We Do</p>
          <h2 className="section-title">OUR SERVICES</h2>
          <div className="gold-line"></div>
          <div className="services-grid">
            {SERVICES_PREVIEW.map((s, i) => {
              const Icon = s.icon;
              return (
                <div className="service-card" key={i}>
                  <span className="service-icon" aria-hidden="true"><Icon /></span>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              );
            })}
          </div>
          <div className="services-cta">
            <button className="btn-outline" onClick={() => navigate('services')}>
              View All Services &amp; Pricing →
            </button>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="why-section page-section">
        <div className="container why-inner">
          <div className="why-image-block">
            <div className="why-img-placeholder">
              <img src="/images/detailing-work.jpg" alt="Cardona Auto detailing work" />
            </div>
            <div className="why-badge">
              <strong>100%</strong>
              <span>Satisfaction Guaranteed</span>
            </div>
          </div>
          <div className="why-text">
            <p className="section-label">Why Cardona</p>
            <h2 className="section-title">WE COME<br />TO YOU</h2>
            <div className="gold-line"></div>
            <p className="why-body">
              No waiting rooms. No drop-offs. Cardona Auto Detailing brings
              professional-grade results straight to your driveway, parking spot, or office.
              Our certified technicians use premium products and take pride in every vehicle.
            </p>
            <ul className="why-list">
              <li><LuCheck className="why-check" aria-hidden="true" />Fully equipped mobile unit</li>
              <li><LuCheck className="why-check" aria-hidden="true" />Premium products — no shortcuts</li>
              <li><LuCheck className="why-check" aria-hidden="true" />Insured &amp; certified technicians</li>
              <li><LuCheck className="why-check" aria-hidden="true" />Before &amp; after photos every time</li>
            </ul>
            <button className="btn-primary" onClick={() => navigate('about')}>
              Learn More About Us →
            </button>
          </div>
        </div>
      </section>

      {/* REVIEW TEASER */}
      <section className="review-teaser page-section">
        <div className="container">
          <p className="section-label">What Clients Say</p>
          <h2 className="section-title">TRUSTED BY<br />CAR LOVERS</h2>
          <div className="gold-line"></div>
          <div className="review-cards">
            {[
              { name: 'John D.', text: 'Absolutely incredible work. My truck looks brand new. Will never go to a traditional car wash again.', stars: 5 },
              { name: 'Maria L.', text: 'Booked the ceramic coating and I\'m blown away. The water just beads right off. Worth every penny.', stars: 5 },
              { name: 'Carlos R.', text: 'The interior cleaning was beyond my expectations. They removed stains I thought were permanent.', stars: 5 },
            ].map((r, i) => (
              <div className="review-card" key={i}>
                <div className="stars" role="img" aria-label={`${r.stars} out of 5 stars`}>{'★'.repeat(r.stars)}</div>
                <p>"{r.text}"</p>
                <strong>— {r.name}</strong>
              </div>
            ))}
          </div>
          <div style={{ textAlign: 'center', marginTop: 40 }}>
            <button className="btn-outline" onClick={() => navigate('reviews')}>
              Read All Reviews →
            </button>
          </div>
        </div>
      </section>

      {/* BOOKING CTA */}
      <section className="booking-cta">
        <div className="container booking-cta-inner">
          <div>
            <h2 className="booking-cta-title">READY FOR A<br /><span>FLAWLESS FINISH?</span></h2>
            <p>Book your appointment online — quick, easy, no phone calls needed.</p>
          </div>
          <button className="btn-primary booking-cta-btn" onClick={() => navigate('reservations')}>
            Schedule Now →
          </button>
        </div>
      </section>
    </div>
  );
}
